/**
 * The MDI keyboard, modelled on a Doosan–FANUC i Series panel.
 *
 * Reference: a photograph of the real control (`coordination/screenshots/` has the panel shots).
 * The layout is not decorative — it is muscle memory for an operator, so it is reproduced in the
 * order it actually appears: a function-key strip with RESET at the far left and SYSTEM at the far
 * right, a QWERTY block, a numeric pad to its right, then CTRL/ALT with the cursor diamond, the
 * PAGE keys, the edit row and INPUT.
 *
 * THE KEYS ARE CREAM WITH DARK LEGENDS because that is what a FANUC pendant looks like. The previous
 * version drew them in the screen's phosphor green, which made the keyboard read as part of the CRT
 * rather than as the plastic below it.
 *
 * WHAT IS LIVE AND WHAT IS FURNITURE. The function keys navigate and RESET clears the alarm. The
 * rest is inert: there is no MDI buffer behind them and there will not be until something can execute
 * a typed block. Inert keys get no press animation and say so on hover, but they are NOT greyed out —
 * on a panel this faithful, a row of disabled-looking keys would look broken rather than honest.
 */

// Top strip. RESET sits alone at the left on the real panel; SYSTEM alone at the right.
const FUNCTION_STRIP = [
  { label: 'RESET', reset: true },
  { label: 'HELP' },
  { label: 'POS',     page: 'POS' },
  { label: 'PROG',    page: 'PROG' },
  { label: 'OFFSET',  page: 'OFFSET', sub: 'SETTING' },
  { label: 'MESSAGE', page: 'ALARM' },
  { label: 'GRAPH' },
  { label: 'CUSTOM',  page: 'TOOL' },
  { label: 'SYSTEM',  page: 'SYSTEM', right: true },
];

const QWERTY = [
  [['Q', '"'], ['W', '@'], ['E', '='], ['R', '*'], ['T', '%'], ['Y', '&'], ['U', "'"], ['I', '('], ['O', ')'], ['P', '#']],
  [['A', '<'], ['S', '>'], ['D', ';'], ['F', ':'], ['G', '?'], ['H', '!'], ['J', '$'], ['K', '['], ['L', ']']],
  [['Z'], ['X'], ['C', '('], ['V', ')'], ['B', '/'], ['N', ','], ['M', 'SP'], ['EOB', ';']],
];

const NUMPAD = [['7', '8', '9'], ['4', '5', '6'], ['1', '2', '3'], ['-', '0', '.']];

function Key({ main, sub, live = false, onClick, title, className = '', style }) {
  return (
    <button
      type="button"
      onClick={live ? onClick : undefined}
      title={title || (live ? undefined : 'Not modelled — no MDI buffer behind this key')}
      className={`fanuc-key ${live ? 'is-live' : 'is-inert'} ${className}`}
      style={style}
    >
      {sub && <span className="fanuc-key-sub">{sub}</span>}
      <span className="fanuc-key-main">{main}</span>
    </button>
  );
}

export default function MdiKeypad({ onPage, onReset, availablePages = [] }) {
  const can = (p) => p && availablePages.includes(p);
  return (
    <div className="fanuc-mdi">
      {/* Function strip */}
      <div className="fanuc-fnrow">
        {FUNCTION_STRIP.map((k) => (
          <Key
            key={k.label}
            main={k.label}
            sub={k.sub}
            live={k.reset ? Boolean(onReset) : can(k.page)}
            onClick={k.reset ? onReset : () => onPage?.(k.page)}
            title={k.reset ? 'Clear the displayed alarm' : can(k.page) ? `Go to ${k.page}` : undefined}
            className={k.right ? 'fanuc-key-right' : ''}
          />
        ))}
      </div>

      <div className="fanuc-mdi-body">
        {/* QWERTY block */}
        <div className="fanuc-qwerty">
          {QWERTY.map((row, r) => (
            <div key={r} className="fanuc-krow" style={{ paddingLeft: r * 12 }}>
              {r === 2 && <Key main="ABC" sub="/abc" />}
              {row.map(([main, sub], i) => <Key key={`${main}-${i}`} main={main} sub={sub} />)}
              {r === 2 && <Key main="SHIFT" />}
            </div>
          ))}
          <div className="fanuc-krow">
            <Key main="CTRL" />
            <Key main="ALT" />
            <div className="fanuc-pagecol">
              <Key main="PAGE" sub="↑" />
              <Key main="PAGE" sub="↓" />
            </div>
            {/* cursor diamond */}
            <div className="fanuc-cursor">
              <span />
              <Key main="↑" />
              <span />
              <Key main="←" />
              <Key main="↓" />
              <Key main="→" />
            </div>
            <div className="fanuc-editcol">
              <Key main="ALTER" />
              <Key main="INSERT" />
              <Key main="DELETE" />
              <Key main="CAN" />
              <Key main="INPUT" className="fanuc-key-wide" />
            </div>
          </div>
        </div>

        {/* Numeric pad */}
        <div className="fanuc-numpad">
          {NUMPAD.flat().map((n, i) => <Key key={`${n}-${i}`} main={n} />)}
        </div>
      </div>
    </div>
  );
}
